import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class SpacetimeHotspotsClassification extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.spacetimehotspotsclassification',
        group: 'Statistics',
        title: 'Spacetime Hotspots Classification',
        description: `
        This component takes the output of Getis Ord Spacetime and classifies each location into specific
        types of hotspots or coldspots, based on patterns of spatial clustering and intensity trends over time.
                `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'datecol',
          type: WorkflowComponentParamType.Column,
          title: 'Date column',
          parent: 'source',
          dataType: [
            ColumnDataType.Datetime,
            ColumnDataType.Timestamp,
            ColumnDataType.Date
          ]
        },
        {
          name: 'valuecol',
          type: WorkflowComponentParamType.Column,
          title: 'Gi Value',
          helper:
            'Select a column that contains a Gi value generated by a Getis Ord Spacetime component',
          default: 'gi',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'pvaluecol',
          type: WorkflowComponentParamType.Column,
          title: 'P Value',
          helper:
            'Select a column  that contains a P value generated by a Getis Ord Spacetime component',
          default: 'p_value',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'threshold',
          type: WorkflowComponentParamType.Selection,
          title: 'Threshold',
          helper:
            'Select the threshold of the P value for a location to be considered as hotspot/coldspot',
          default: 0.05,
          options: [0.01, 0.05, 0.1, 1]
        },
        {
          name: 'algorithm',
          type: WorkflowComponentParamType.Selection,
          title: 'Algorithm',
          helper: 'The algorithm to be used for the monotonic trend test',
          default: 'Mann-Kendall',
          options: ['Mann-Kendall', 'Modified Mann-Kendall']
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      'result',
      codeType
    )
    const source = preparedParams.source as string
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)

    const options = {
      threshold: params.threshold,
      algorithm: params.algorithm === 'Mann-Kendall' ? 'mk' : 'mmk'
    }
    let jsonSqlQuotes = `'''`
    let inputQuery = dedent`SELECT * EXCEPT (${params.datecol}), CAST(${
      params.datecol
    } AS DATETIME) AS ${params.datecol} FROM ${connection.quoteName(source)}`
    if (connection.type === Provider.Snowflake) {
      jsonSqlQuotes = `'`
      inputQuery = dedent`SELECT * EXCLUDE (${params.datecol}), TO_TIMESTAMP(${
        params.datecol
      }) AS ${params.datecol} FROM ${connection.quoteName(source)}`
    }

    let query = ''
    let outTable = tablename
    let copyFromTempTable = ''
    if (
      connection.type === Provider.BigQuery &&
      codeType === WorkflowCodeType.StoredProcedure
    ) {
      outTable = `${connection.tempLocation}.${tempTableName(
        componentName,
        preparedParams.source
      )}`
      copyFromTempTable = connection.getCreateTempTableQuery(
        tablename,
        `SELECT * FROM ${connection.quoteName(outTable)}`,
        codeType,
        null
      )
    }

    query = dedent`
      CALL ${at}.SPACETIME_HOTSPOTS_CLASSIFICATION(
          '${inputQuery}',
          '${outTable}',
          '${params.indexcol as string}',
          '${params.datecol as string}',
          '${params.valuecol as string}',
          '${params.pvaluecol as string}',
          ${jsonSqlQuotes}
          ${JSON.stringify(options, null, 2)}
          ${jsonSqlQuotes}
        );
      ${copyFromTempTable}
      `

    query = connection.wrapCodeInIfTableExistsBlock(
      query,
      [tablename],
      codeType
    )

    return {
      outputs: { result: tablename },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const indexCol = columns.source.find((col) => col.name === params.indexcol)
    if (indexCol == undefined) {
      return []
    }
    return [
      {
        name: 'classification',
        type: ColumnDataType.String
      },
      {
        name: 'tau',
        type: ColumnDataType.Number
      },
      {
        name: 'tau_p',
        type: ColumnDataType.Number
      },
      indexCol
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { SpacetimeHotspotsClassification }
